// ============================================================================
// Instagram DM Adapter — public surface + registration helper.
//
// This file is the one the rest of the module imports. At module-load time,
// `registerInstagramDmAdapter()` wires the implementation into BOTH
// registries:
//
//   1. `webhook-adapters` (slug-keyed) — powers the public ingress route
//      `POST /api/v1/connectors/webhooks/instagram-dm/:instanceId`.
//   2. `capability-registry` (typeId-keyed) — lets any cross-module caller
//      reach Instagram DM via `call(instanceId, 'messaging', 'sendMessage', ...)`.
//
// Matching up the keys: the `004_instagram_dm_seed.sql` migration sets
// `connectors_types.id = connectors_types.slug = 'instagram-dm'`, so the same
// string addresses the row from every angle (instance.type_id, URL path,
// capability register, webhook register).
// ============================================================================

import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import {
  buildInstagramDmMessagingMethods,
  type InstagramDmMessagingMethodsDeps,
} from './capability-methods'
import { instagramWebhookAdapter } from './webhook'

/** Canonical type id / slug. MUST match the `connectors_types` seed row. */
export const INSTAGRAM_DM_TYPE_SLUG = 'instagram-dm'

export interface RegisterInstagramDmOpts extends InstagramDmMessagingMethodsDeps {}

/**
 * Register the Instagram DM adapter in both the webhook and capability
 * registries. Idempotent: re-calling just overwrites the prior registration
 * (matches the semantics of the underlying registries). Safe to call at
 * module-load + test-reset points without special-casing.
 */
export function registerInstagramDmAdapter(
  opts: RegisterInstagramDmOpts = {},
): void {
  registerWebhookAdapter(INSTAGRAM_DM_TYPE_SLUG, instagramWebhookAdapter)
  registerCapability(
    INSTAGRAM_DM_TYPE_SLUG,
    'messaging',
    buildInstagramDmMessagingMethods(opts),
  )
}

// ============================================================================
// Barrel — re-export the pieces consumers might reach for directly.
// ============================================================================

export {
  instagramWebhookAdapter,
  verifyInstagramSignature,
  parseInstagramWebhook,
} from './webhook'

export {
  buildInstagramDmMessagingMethods,
  type InstagramDmMessagingMethodsDeps,
} from './capability-methods'

export {
  IG_OAUTH_AUTHORIZE_URL,
  IG_OAUTH_TOKEN_URL,
  IG_GRAPH_API_BASE,
  INSTAGRAM_DM_DEFAULT_SCOPES,
  buildAuthorizeUrl,
  exchangeCode,
  extendToInstagramLongLivedToken,
  refreshInstagramLongLivedToken,
  redactInstagramSecrets,
  InstagramDmOAuthError,
  type InstagramAuthorizeInput,
  type InstagramAuthorizeResult,
  type ExchangeCodeInput,
  type ShortLivedInstagramToken,
  type ExtendInstagramTokenInput,
  type LongLivedInstagramToken,
  type RefreshInstagramTokenInput,
  type InstagramDmOAuthErrorCode,
} from './oauth'

export {
  sendInstagramMessage,
  sendInstagramTyping,
  type SendInstagramMessageInput,
  type SendInstagramMessageOutput,
  type SendInstagramTypingInput,
} from './send'

export { getInstagramUserProfile, fetchUserProfile } from './profile'

export {
  InstagramDmError,
  InstagramDmSignatureError,
  classifyInstagramError,
  parseInstagramRetryAfterMs,
  sanitizeInstagramErrorMessage,
  type InstagramDmErrorCode,
  type InstagramDmErrorDetails,
  type InstagramSendResponse,
  type InstagramUserProfile,
  type InstagramGraphError,
  type InstagramErrorEnvelope,
} from './types'
